import { Component, OnDestroy, OnInit } from '@angular/core';
import { NotificationsHttpService } from '$modules/personal/notifications/notifications-http.service';
import {
  Notification,
  NotificationsParams,
} from '$modules/personal/notifications/model/notifications-model';
import { BehaviorSubject } from 'rxjs';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import { catchError, finalize, tap } from 'rxjs/operators';
import { Pagination } from '$core/models/pagination';
import * as dayjs from 'dayjs';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationsDataService } from '$modules/personal/notifications/notifications-data.service';
import { BsModalService } from 'ngx-bootstrap/modal';
// tslint:disable-next-line:max-line-length
import { NotificationSettingsModalComponent } from '$modules/personal/notifications/components/notification-settings-modal/notification-settings-modal.component';
import { SwalService } from '$core/shell/swal/swal.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-notifications-index',
  templateUrl: './notifications-index.component.html',
  styleUrls: ['./notifications-index.component.scss'],
})
export class NotificationsIndexComponent implements OnInit, OnDestroy {
  public notifications$ = new BehaviorSubject<Pagination<Notification> | null>(
    null
  );
  private queryParams: NotificationsParams = {
    page: 1,
    per_page: 12,
  };
  public isFirstLoading$ = new BehaviorSubject<boolean>(false);
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public isInfinityScrollLoading$ = new BehaviorSubject<boolean>(false);
  public readonly dayjs = dayjs;
  public fallbackImgIds = new Set<string>();

  constructor(
    private readonly router: Router,
    private readonly notificationsHttpService: NotificationsHttpService,
    private readonly notificationsDataService: NotificationsDataService,
    private readonly route: ActivatedRoute,
    private readonly modalService: BsModalService,
    protected swalService: SwalService,
    protected translateService: TranslateService
  ) {}

  public ngOnInit() {
    this.getNotifications(this.queryParams);
    this.updateUrlPageParam(this.queryParams);
    this.notificationsDataService.refresh$.next();
  }

  public getNotifications(params: NotificationsParams): void {
    this.isFirstLoading$.next(true);

    this.notificationsHttpService
      .getNotificationsIndex({ params })
      .pipe(
        tap((res) => {
          if (!res || !('data' in res)) {
            return;
          }
          this.notifications$.next(res);
        }),
        untilDestroyed(this),
        finalize(() => this.isFirstLoading$.next(false)),
        catchError((error: HttpErrorResponse) => {
          const swal = this.swalService.swal;
          swal.icon = 'error';
          swal.title = this.translateService.instant('AN_ERROR_HAS_OCCURRED');

          swal.text = error.error.message;
          swal.fire();

          throw error;
        })
      )
      .subscribe((res) => {
        this.notifications$.next(res);
      });
  }

  public onScroll() {
    const notificationsData = this.notifications$.getValue();
    if (notificationsData && notificationsData.next_page_url) {
      this.queryParams.page++;
      this.updateUrlPageParam(this.queryParams);
      this.nextPage(notificationsData.next_page_url);
    }
  }

  private nextPage(nextPageUrl: string | null): void {
    if (!nextPageUrl) {
      return;
    }

    this.isInfinityScrollLoading$.next(true);
    this.notificationsHttpService
      .getNotificationsIndex({ url: nextPageUrl })
      .pipe(
        tap((res) => {
          if (!res || !('data' in res)) {
            return;
          }
          const current = this.notifications$.getValue();
          if (!current) {
            this.notifications$.next(res);
          } else {
            this.notifications$.next({
              ...res,
              data: [...current.data, ...res.data],
            });
          }
        }),
        untilDestroyed(this),
        finalize(() => this.isInfinityScrollLoading$.next(false)),
        catchError((error: HttpErrorResponse) => {
          const swal = this.swalService.swal;
          swal.icon = 'error';
          swal.title = this.translateService.instant('AN_ERROR_HAS_OCCURRED');
          swal.text = error.statusText;
          swal.fire();

          throw error;
        })
      )
      .subscribe((res) => {
        const current = this.notifications$.getValue();
        if (!current) {
          this.notifications$.next(res);
        } else {
          this.notifications$.next({
            ...res,
            data: [...current.data, ...res.data],
          });
        }
      });
  }

  public navigateToNotificationItem(messageId: string): void {
    void this.router.navigate([`/personal/notifications/${messageId}`]);
  }

  public makeAllAsRead(): void {
    this.isLoading$.next(true);
    this.notificationsHttpService
      .makeAllAsRead()
      .pipe(
        untilDestroyed(this),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe(() => {
        this.queryParams.page = 1;
        this.getNotifications(this.queryParams);
        this.notificationsDataService.refresh$.next();
      });
  }

  private updateUrlPageParam(queryParams: NotificationsParams): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  public openSettingsModal(): void {
    const modal = this.modalService.show(NotificationSettingsModalComponent, {
      ignoreBackdropClick: false,
      class: 'modal-dialog-centered modal-lg',
    });

    modal.content.onClose
      .pipe(untilDestroyed(this))
      .subscribe((res: { is_accept_advertisement: 0 | 1 }) => {
        if (
          res.is_accept_advertisement === 0 &&
          this.notifications$.getValue().data &&
          this.notifications$.getValue().data.length > 0
        ) {
          this.getNotifications(this.queryParams);
        }
      });
  }

  public enableFallbackImg(notificationId: string): void {
    this.fallbackImgIds.add(notificationId);
  }

  public ngOnDestroy(): void {}
}
